<template>
    <div class="CInlineDatePicker flex gap-3">
        <CFormInput
            v-if="hasDayPicker"
            v-model="day"
            class="day"
            data-test-id="day"
            :disabled="disabled"
            max="31"
            maxlength="2"
            min="1"
            placeholder="deň"
            type="number"
            @update:model-value="update"
        />

        <CFormSelect
            allow-empty
            class="month"
            data-test-id="month"
            :disabled="disabled"
            label-key="name"
            :options="availableMonths"
            placeholder="mesiac"
            :value="month"
            value-key="month"
            @input="
                (val) => {
                    month = val
                    update()
                }
            "
        />

        <CFormSelect
            v-if="availableYears"
            allow-empty
            class="year"
            data-test-id="year"
            :disabled="disabled"
            :options="availableYears"
            placeholder="rok"
            :value="year"
            @input="
                (val) => {
                    year = val
                    update()
                }
            "
        />

        <CFormInput
            v-else
            v-model="year"
            class="col-span-2"
            data-test-id="year"
            :disabled="disabled"
            placeholder="rok"
            type="number"
            @update:model-value="update"
        />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import 'dayjs/locale/sk'
import { Timestamp } from 'firebase/firestore'
import range from 'lodash-es/range'
import { ref, computed, watch, onMounted, nextTick } from 'vue'

interface Props {
    modelValue?: string | Date | Timestamp
    type?: 'month'
    availableYears?: number[]
    invalid?: boolean
    disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: undefined,
    type: undefined,
    availableYears: undefined,
    invalid: false,
    disabled: false
})

const emit = defineEmits<{
    (e: 'update:modelValue', value: Date | undefined): void
}>()

dayjs.locale('sk')

const month = ref<string>('')
const day = ref<string>('')
const year = ref<string>('')
const monthObject = ref<any>(null)

const hasDayPicker = computed(() => props.type !== 'month')

const availableMonths = computed(() => {
    const date = dayjs()
    return range(12).map((monthIndex) => ({
        month: monthIndex + 1,
        name: date.month(monthIndex).locale('sk').format('MMMM')
    }))
})

function update() {
    const yearNum = Number(year.value)
    const monthNum = Number(month.value)
    const dayNum = hasDayPicker.value ? Number(day.value) : 1

    if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum)) {
        console.error('Invalid number values:', { year: year.value, month: month.value, day: day.value })
        return
    }

    try {
        const date = new Date(yearNum, monthNum - 1, dayNum)

        if (isNaN(date.getTime())) {
            console.error('Invalid date timestamp')
            return
        }

        nextTick(() => {
            year.value = date.getFullYear().toString()
            month.value = (date.getMonth() + 1).toString()
            day.value = date.getDate().toString().padStart(2, '0')
        })

        emit('update:modelValue', date)
    } catch (error) {
        console.error('Error creating date:', error)
    }
}

function parseCurrentDate() {
    if (!props.modelValue) {
        day.value = ''
        month.value = ''
        year.value = ''
        monthObject.value = null
        return
    }

    let date: Date
    if (props.modelValue instanceof Timestamp) {
        date = props.modelValue.toDate()
    } else if (props.modelValue instanceof Date) {
        date = props.modelValue
    } else {
        date = new Date(props.modelValue)
    }

    if (isNaN(date.getTime())) {
        console.error('Invalid date:', date)
        return
    }

    day.value = date.getDate().toString().padStart(2, '0')
    month.value = (date.getMonth() + 1).toString()
    year.value = date.getFullYear().toString()

    nextTick(() => {
        monthObject.value = availableMonths.value.find((m) => m.month === Number(month.value))
    })
}

watch(
    () => props.modelValue,
    (newValue) => {
        if (!newValue) {
            day.value = ''
            month.value = ''
            year.value = ''
            monthObject.value = null
            return
        }

        const date = newValue instanceof Timestamp ? newValue.toDate() : new Date(newValue)

        if (isNaN(date.getTime())) {
            console.error('Invalid date in props')
            return
        }

        day.value = date.getDate().toString().padStart(2, '0')
        month.value = (date.getMonth() + 1).toString()
        year.value = date.getFullYear().toString()
        monthObject.value = availableMonths.value.find((m) => m.month === Number(month.value))
    },
    { immediate: true }
)

onMounted(() => {
    parseCurrentDate()
})
</script>

<style lang="postcss" scoped>
.CInlineDatePicker {
    .day {
        width: 90px;
    }

    .month {
        width: 200px;
    }

    .year {
        width: 136px;
    }
}
</style>
